'use client';

import {
	ChevronDown,
	Settings,
	LogOut
} from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { signOut } from 'next-auth/react';
import { Session } from 'next-auth';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

// Authenticated navigation for logged-in users
export function NosleadAuthenticatedNavigation({ session }: { session: Session | null }) {
	const pathname = usePathname();

	const getInitials = (name?: string | null, email?: string | null) => {
		if (name) {
			return name
				.split(' ')
				.map((n) => n[0])
				.join('')
				.toUpperCase()
				.slice(0, 2);
		}
		if (email) {
			return email.slice(0, 2).toUpperCase();
		}
		return 'U';
	};

	return (
		<nav className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex justify-between items-center h-16">
					{/* Logo */}
					<div className="flex items-center">
						<Link
							href="/"
							className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
						>
							<div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shadow-md">
								<span className="text-white font-bold text-xl tracking-wider">
									NOS
								</span>
							</div>
							<span className="text-xl font-semibold text-gray-900">Lead</span>
						</Link>
					</div>

					{/* User Menu */}
					<div className="flex items-center space-x-4">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									className="flex items-center space-x-2 hover:bg-gray-50"
								>
									<Avatar className="w-8 h-8">
										<AvatarImage src={session?.user?.image || ''} />
										<AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white text-sm">
											{getInitials(session?.user?.name, session?.user?.email)}
										</AvatarFallback>
									</Avatar>
									<span className="hidden md:block text-sm font-medium text-gray-700">
										{session?.user?.name || session?.user?.email}
									</span>
									<ChevronDown className="w-4 h-4 text-gray-500" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-56">
								<DropdownMenuItem asChild>
									<Link href="/settings" className="flex items-center">
										<Settings className="w-4 h-4 mr-2" />
										Settings
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									onClick={() => signOut({ callbackUrl: '/' })}
									className="text-red-600 focus:text-red-600 focus:bg-red-50"
								>
									<LogOut className="w-4 h-4 mr-2" />
									Sign out
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>
			</div>
		</nav>
	);
}
